import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Spacing from '../Spacing';
import SectionHeadingStyle3 from '../SectionHeading/SectionHeadingStyle3';
import { pageTitle } from '../../helpers/PageTitle';
import { Icon } from '@iconify/react';

export default function ContactPage() {
  pageTitle('Contact');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    subject: false,
    message: false,
  });

  const [status, setStatus] = useState({ loading: false, success: null });
  const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3000';

  // Real-time validation effect
  useEffect(() => {
    if (touched.name) {
      setErrors((prev) => ({
        ...prev,
        name: validateName(formData.name),
      }));
    }

    if (touched.email) {
      setErrors((prev) => ({
        ...prev,
        email: validateEmail(formData.email),
      }));
    }

    if (touched.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: validatePhoneNumber(formData.phone),
      }));
    }

    if (touched.subject) {
      setErrors((prev) => ({
        ...prev,
        subject: validateSubject(formData.subject),
      }));
    }

    if (touched.message) {
      setErrors((prev) => ({
        ...prev,
        message: validateMessage(formData.message),
      }));
    }
  }, [formData, touched]);

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[A-Za-z\s]+$/.test(name))
      return 'Name should contain only letters and spaces';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhoneNumber = (phone) => {
    if (!phone.trim()) return 'Phone number is required';

    const cleanedPhone = phone.replace(/[^\d+]/g, '');

    if (cleanedPhone.startsWith('+')) {
      if (cleanedPhone.length < 9) return 'International number is too short';
      if (cleanedPhone.length > 16) return 'Phone number is too long';
    } else {
      if (cleanedPhone.length < 8) return 'Phone number is too short';
      if (cleanedPhone.length > 15) return 'Phone number is too long';
    }
    return '';
  };

  const validateSubject = (subject) => {
    if (!subject.trim()) return 'Subject is required';
    if (subject.trim().length < 3)
      return 'Subject must be at least 3 characters';
    return '';
  };

  const validateMessage = (message) => {
    if (!message.trim()) return 'Message is required';
    if (message.trim().length < 10)
      return 'Message must be at least 10 characters';
    return '';
  };

  // Validate all fields and return if the form is valid
  const validateForm = () => {
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhoneNumber(formData.phone),
      subject: validateSubject(formData.subject),
      message: validateMessage(formData.message),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const cleanedValue = value.replace(/[^\d\s+()-]/g, '');
      let formattedValue = cleanedValue;

      if (cleanedValue.includes('+')) {
        formattedValue =
          '+' + cleanedValue.replace(/\+/g, '').replace(/^\+/, '');
      }

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else if (name === 'name') {
      const cleanedValue = value.replace(/[^A-Za-z\s]/g, '');

      setFormData((prev) => ({
        ...prev,
        [name]: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    setTouched(allTouched);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setStatus({ loading: true, success: null });

    try {
      const response = await axios.post(`/api/mail/contact`, formData);

      setStatus({ loading: false, success: true });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTouched({
        name: false,
        email: false,
        phone: false,
        subject: false,
        message: false,
      });
      setErrors({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setStatus({ loading: false, success: null });
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      console.log(
        'Error response details:',
        error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : 'No response data available'
      );

      setStatus({ loading: false, success: false });

      // Clear error message after 3 seconds
      setTimeout(() => {
        setStatus({ loading: false, success: null });
      }, 3000);
    }
  };

  // Helper function to determine input border color based on validation state
  const getInputClass = (fieldName) => {
    const baseClass = 'cs_form_field';
    if (!touched[fieldName]) return baseClass;
    return errors[fieldName] ? `${baseClass} error` : `${baseClass} valid`;
  };

  // Check if form is valid for button enabling/disabling
  const isFormValid =
    !Object.values(errors).some((error) => error) &&
    Object.values(formData).every((value) => value.trim() !== '');

  return (
    <>
      <Spacing lg='70' md='70' />
      <Spacing lg='140' md='80' />
      <SectionHeadingStyle3
        title='Get in touch for more <br/>information and support'
        subTitle='Contact'
        shape='shape_6'
      />
      <Spacing lg='75' md='60' />
      <section>
        <div className='container'>
          <div className='row align-items-center cs_gap_y_45'>
            <div className='col-lg-6'>
              <h2 className='cs_fs_50'>
                Come & visit <br />
                our place!
              </h2>
              <div className='cs_height_55 cs_height_lg_30' />
              <ul className='cs_mp0 cs_contact_info'>
                <li>
                  <h3 className='cs_fs_29 cs_semibold'>Email:</h3>
                  <p className='mb-0'>info@ranads.com</p>
                </li>
                <li>
                  <h3 className='cs_fs_29 cs_semibold'>Phone:</h3>
                  <p className='mb-0'>+961 70 080 040</p>
                </li>
                <li>
                  <h3 className='cs_fs_29 cs_semibold'>Address:</h3>
                  <p className='mb-0'>
                    Charles Malek Avenue, Achrafiyeh, Lebanon
                  </p>
                </li>
              </ul>
            </div>
            <div className='col-lg-6'>
              <div className='cs_contact_form_wrap'>
                <div className='cs_gray_bg_3 cs_contact_form_bg' />
                <form className='cs_contact_form' onSubmit={handleSubmit}>
                  <label className='cs_fs_21 cs_semibold cs_primary_color'>
                    Your full name
                  </label>
                  <input
                    placeholder='Type your name'
                    type='text'
                    name='name'
                    className={getInputClass('name')}
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-invalid={errors.name ? 'true' : 'false'}
                  />
                  {touched.name && errors.name && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '5px',
                      }}
                    >
                      {errors.name}
                    </div>
                  )}
                  <div className='cs_height_38 cs_height_lg_25' />

                  <label className='cs_fs_21 cs_semibold cs_primary_color'>
                    Give valid email
                  </label>
                  <input
                    placeholder='Type your email address'
                    type='email'
                    name='email'
                    className={getInputClass('email')}
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-invalid={errors.email ? 'true' : 'false'}
                  />
                  {touched.email && errors.email && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '5px',
                      }}
                    >
                      {errors.email}
                    </div>
                  )}
                  <div className='cs_height_38 cs_height_lg_25' />

                  <label className='cs_fs_21 cs_semibold cs_primary_color'>
                    Phone number
                  </label>
                  <input
                    placeholder='Type your phone number'
                    type='tel'
                    name='phone'
                    className={getInputClass('phone')}
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                  />
                  {touched.phone && errors.phone && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '5px',
                      }}
                    >
                      {errors.phone}
                    </div>
                  )}
                  <div className='cs_height_38 cs_height_lg_25' />

                  <label className='cs_fs_21 cs_semibold cs_primary_color'>
                    Subject
                  </label>
                  <input
                    placeholder='What is this about?'
                    type='text'
                    name='subject'
                    className={getInputClass('subject')}
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-invalid={errors.subject ? 'true' : 'false'}
                  />
                  {touched.subject && errors.subject && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '5px',
                      }}
                    >
                      {errors.subject}
                    </div>
                  )}
                  <div className='cs_height_38 cs_height_lg_25' />

                  <label className='cs_fs_21 cs_semibold cs_primary_color'>
                    Project brief
                  </label>
                  <textarea
                    cols={30}
                    rows={5}
                    name='message'
                    className={getInputClass('message')}
                    placeholder='Tell us about your project'
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-invalid={errors.message ? 'true' : 'false'}
                  />
                  {touched.message && errors.message && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '5px',
                      }}
                    >
                      {errors.message}
                    </div>
                  )}
                  <div className='cs_height_38 cs_height_lg_25' />

                  <button
                    className='cs_btn cs_style_1'
                    type='submit'
                    disabled={!isFormValid || status.loading}
                    style={{
                      opacity: !isFormValid || status.loading ? 0.7 : 1,
                      cursor:
                        !isFormValid || status.loading
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    {status.loading ? 'Sending...' : 'Send Message'}
                    <span>
                      <i>
                        <Icon icon='fa6-solid:arrow-right' />
                      </i>
                      <i>
                        <Icon icon='fa6-solid:arrow-right' />
                      </i>
                    </span>
                  </button>

                  {status.success === true && (
                    <div
                      style={{
                        color: '#22c55e',
                        fontSize: '14px',
                        marginTop: '15px',
                        fontWeight: '500',
                      }}
                    >
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}
                  {status.success === false && (
                    <div
                      style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '15px',
                        fontWeight: '500',
                      }}
                    >
                      An error occurred. Please try again later.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className='cs_height_0 cs_height_lg_80' />
      </section>
      <div className='cs_map'>
        <iframe
          id='map'
          src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d827.9776093475256!2d35.522194169662626!3d33.8919603983261!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f1657cda30ab9%3A0x3daf67f84976171c!2sSagesse%2C%20Bayrut!5e0!3m2!1sen!2slb!4v1760445064160!5m2!1sen!2slb'
          allowFullScreen
          title='Google Map - Ranads Location'
        />
      </div>
    </>
  );
}
